import React from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Printer, 
  X, 
  ShieldCheck, 
  Gem, 
  CheckCircle2, 
  Download,
  Building2,
  FileText
} from 'lucide-react';

export default function AdminInvoiceModal() {
  const { invoiceModalOrder, setInvoiceModalOrder, storeSettings } = useShop();

  if (!invoiceModalOrder) return null;

  const order = invoiceModalOrder;
  const subtotal = order.items?.reduce((sum, it) => sum + (it.price * (it.qty || 1)), 0) || order.total || 0;
  const discount = order.discountAmount || 0;
  const taxableValue = subtotal - discount;
  const cgst = Math.round(taxableValue * 0.015); // 1.5% CGST
  const sgst = Math.round(taxableValue * 0.015); // 1.5% SGST
  const totalAmount = order.total || (taxableValue + cgst + sgst);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="invoice-modal-overlay" style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem',
      overflowY: 'auto'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '850px',
        maxHeight: '94vh',
        backgroundColor: '#ffffff',
        color: '#1a1a1a',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.7)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        
        {/* Top Modal Controls (Hidden during print) */}
        <div className="no-print" style={{
          backgroundColor: '#101018',
          borderBottom: '1px solid var(--border-gold)',
          padding: '0.9rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <FileText size={18} color="var(--gold-400)" />
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
              Official Tax Invoice & BIS Hallmark Certificate
            </span>
            <span style={{
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid var(--border-gold)',
              color: 'var(--gold-300)',
              fontSize: '0.72rem',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontWeight: 700
            }}>
              {order.id}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={handlePrint}
              className="btn btn-gold btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}
            >
              <Printer size={15} />
              <span>Print A4 Invoice</span>
            </button>
            <button
              onClick={() => setInvoiceModalOrder(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '0.3rem',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Printable Invoice Document Body */}
        <div id="printable-invoice" style={{
          padding: '2.5rem 3rem',
          overflowY: 'auto',
          backgroundColor: '#fff',
          fontFamily: "'Inter', sans-serif",
          color: '#111827',
          lineHeight: 1.5
        }}>
          
          {/* Invoice Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '2px solid #b8860b',
            paddingBottom: '1.2rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                <span style={{
                  fontSize: '1.45rem',
                  fontWeight: 900,
                  letterSpacing: '0.04em',
                  color: '#92400e',
                  fontFamily: 'Georgia, serif'
                }}>
                  {storeSettings.storeName.toUpperCase()}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#4b5563', maxWidth: '380px' }}>
                {storeSettings.address}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '0.2rem' }}>
                <strong>Phone:</strong> {storeSettings.phone} | <strong>Email:</strong> {storeSettings.email}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#1f2937', marginTop: '0.2rem' }}>
                <strong>GSTIN:</strong> {storeSettings.gstin} | <strong>BIS License:</strong> {storeSettings.bisLicense}
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{
                background: '#fef3c7',
                border: '1px solid #d97706',
                color: '#92400e',
                fontWeight: 800,
                fontSize: '0.85rem',
                padding: '0.3rem 0.9rem',
                borderRadius: '4px',
                display: 'inline-block',
                marginBottom: '0.5rem',
                textTransform: 'uppercase'
              }}>
                TAX INVOICE & CASH MEMO
              </div>
              <div style={{ fontSize: '0.82rem', color: '#374151' }}>
                <strong>Invoice No:</strong> INV-{String(order.id || '').replace(/[^0-9]/g, '') || '90218'}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#374151' }}>
                <strong>Date:</strong> {order.date || '04 Sep 2026'}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#059669', fontWeight: 700, marginTop: '0.2rem' }}>
                Payment Status: {order.paymentMethod || 'UPI / Prepaid Verified'}
              </div>
            </div>
          </div>

          {/* Customer Details & Shipment Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '1.5rem',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '1rem 1.2rem',
            marginBottom: '1.5rem',
            fontSize: '0.8rem'
          }}>
            <div>
              <div style={{ fontWeight: 800, color: '#92400e', marginBottom: '0.3rem', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                Billed & Shipped To:
              </div>
              <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#111827' }}>
                {order.customer?.name || 'Valued Patron'}
              </div>
              <div style={{ color: '#4b5563', marginTop: '0.15rem' }}>
                {order.customer?.address || 'Showroom Counter Pick-up'}
              </div>
              <div style={{ color: '#4b5563', marginTop: '0.15rem' }}>
                <strong>Contact:</strong> +91 {order.customer?.phone || 'N/A'} {order.customer?.email ? `| ${order.customer?.email}` : ''}
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 800, color: '#92400e', marginBottom: '0.3rem', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                Dispatch & Compliance:
              </div>
              <div><strong>Place of Supply:</strong> Rajasthan (State Code 08)</div>
              <div><strong>Fulfillment Hub:</strong> Vault Custody Dispatch</div>
              <div><strong>Logistics Partner:</strong> {order.courier || 'BVC Logistics Armored Courier'}</div>
              <div><strong>Tracking AWB:</strong> {order.trackingNumber || 'BVC-SEC-921415'}</div>
            </div>
          </div>

          {/* Items Table */}
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '1.5rem',
            fontSize: '0.8rem'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#111827', color: '#fef3c7' }}>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'center', border: '1px solid #374151', width: '35px' }}>#</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'left', border: '1px solid #374151' }}>Item Description</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'center', border: '1px solid #374151', width: '70px' }}>HSN</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'center', border: '1px solid #374151', width: '90px' }}>Purity</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'center', border: '1px solid #374151', width: '45px' }}>Qty</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'right', border: '1px solid #374151', width: '100px' }}>Rate (₹)</th>
                <th style={{ padding: '0.6rem 0.8rem', textAlign: 'right', border: '1px solid #374151', width: '110px' }}>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'center', border: '1px solid #e5e7eb' }}>{idx + 1}</td>
                  <td style={{ padding: '0.65rem 0.8rem', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontWeight: 700, color: '#111827' }}>{item.name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                      {item.metal || 'BIS 916 Hallmarked Gold'} • Tamper-proof RFID Sealed
                    </div>
                  </td>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'center', border: '1px solid #e5e7eb', color: '#4b5563' }}>
                    {item.hsn || (item.metal?.toLowerCase().includes('silver') ? '7114' : item.metal?.toLowerCase().includes('steel') ? '7323' : '7113')}
                  </td>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'center', border: '1px solid #e5e7eb', fontWeight: 600 }}>
                    {item.metal?.includes('22K') ? '22K (91.6%)' : item.metal?.includes('18K') ? '18K (75.0%)' : item.metal?.includes('925') ? '925 (92.5%)' : 'Hallmarked'}
                  </td>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'center', border: '1px solid #e5e7eb' }}>
                    {item.qty || 1}
                  </td>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', border: '1px solid #e5e7eb' }}>
                    {formatINR(item.price)}
                  </td>
                  <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', border: '1px solid #e5e7eb', fontWeight: 700 }}>
                    {formatINR(item.price * (item.qty || 1))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Calculations Summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '1.5rem',
            alignItems: 'flex-start',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              background: '#fefce8',
              border: '1px solid #fef08a',
              borderRadius: '6px',
              padding: '0.9rem',
              fontSize: '0.74rem',
              color: '#713f12'
            }}>
              <div style={{ fontWeight: 800, marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <ShieldCheck size={16} color="#ca8a04" /> BIS HALLMARK & AUTHENTICITY CERTIFICATE
              </div>
              <p style={{ margin: 0 }}>
                Certified that the articles described herein adhere strictly to the Bureau of Indian Standards (BIS) Hallmarking Act. Each item carries the BIS Tri-mark, Purity Fineness Code, and Assaying Center unique identification mark.
              </p>
            </div>

            <div style={{ fontSize: '0.82rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', color: '#4b5563' }}>
                <span>Subtotal (Gross Value):</span>
                <span>{formatINR(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', color: '#059669', fontWeight: 600 }}>
                  <span>Privilege Discount:</span>
                  <span>- {formatINR(discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', color: '#4b5563' }}>
                <span>Taxable Amount:</span>
                <span>{formatINR(taxableValue)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', color: '#4b5563' }}>
                <span>CGST (1.5%):</span>
                <span>{formatINR(cgst)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', color: '#4b5563' }}>
                <span>SGST (1.5%):</span>
                <span>{formatINR(sgst)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.6rem 0',
                borderTop: '2px solid #111827',
                borderBottom: '2px solid #111827',
                fontWeight: 900,
                fontSize: '1.05rem',
                color: '#111827',
                marginTop: '0.3rem'
              }}>
                <span>Total Payable:</span>
                <span style={{ color: '#92400e' }}>{formatINR(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Signatures & Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '1.5rem',
            marginTop: '1rem',
            fontSize: '0.74rem',
            color: '#6b7280'
          }}>
            <div>
              <div><strong>Terms & Conditions:</strong></div>
              <div>• Lifetime 100% exchange on gold value based on current daily bullion rate.</div>
              <div>• Goods once inspected and sold are covered by comprehensive vault transit insurance.</div>
              <div>• Subject to Jaipur Jurisdiction only.</div>
            </div>

            <div style={{ textAlign: 'center', minWidth: '180px' }}>
              <div style={{ fontStyle: 'italic', color: '#92400e', marginBottom: '1.8rem', fontFamily: 'cursive', fontSize: '0.9rem' }}>
                M.L. Jewellers Vault Authority
              </div>
              <div style={{ borderTop: '1px dashed #374151', paddingTop: '0.3rem', fontWeight: 700, color: '#111827' }}>
                Authorised Signatory
              </div>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-invoice, #printable-invoice * {
            visibility: visible;
          }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 20mm;
            background: white !important;
            color: black !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
